// ResponseMessage.tsx
import { useEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { komplyanceAiChatMessage } from '@app/pages';
import { CopyButton } from '@components/CopyButton/CopyButton';
import { FeedbackButtons } from '@components/FeedBackButton/FeedbackButtons';
import { type CitationSource, Markdown } from '@components/Markdown';
import { FeedbackRating, KomplyanceAiMessageEndRetrieverResource } from '@libs/types';
import { detectDirection } from '@libs/utils';
import { NoFlipBox, NoFlipPaper } from '@utils';

import { linkifyCitations, STREAMING_STAGE_FALLBACK, STREAMING_STAGE_LABELS } from '../../utils';
import { ReferenceItem, References } from '../References';

interface ResponseBubbleProps {
  message: komplyanceAiChatMessage;
  onFeedback?: (messageId: string, rating: FeedbackRating) => void;
  isFeedbackSubmitting?: boolean;
  isStreaming?: boolean;
}

const formatDocumentName = (name: string): string => {
  return name
    .replace(/\+/g, ' ')
    .replace(/_/g, ' ')
    .replace(/\.md$/i, '');
};

const transformReferences = (
  references: KomplyanceAiMessageEndRetrieverResource[] = [],
): ReferenceItem[] => {
  // Deliberately not filtered. Card n is this array's nth entry, while the `[n]`
  // markers in the answer are numbered from the server's resource list, so
  // dropping an entry here would shift every later card and point each marker at
  // the wrong source. The server already omits sources with no URL, and a card
  // that somehow arrives without one renders non-clickable rather than vanishing.
  return references.map((doc: KomplyanceAiMessageEndRetrieverResource) => ({
    id: doc.document_id,
    datasetId: doc.dataset_id,
    name: formatDocumentName(doc.document_name),
    url: doc.doc_metadata?.document_url as string | undefined,
  }));
};

export const ResponseBubble: React.FC<ResponseBubbleProps> = observer(
  ({ message, onFeedback, isFeedbackSubmitting = false, isStreaming = false }) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
      if (videoRef.current) {
        videoRef.current.play().catch((e) => console.warn('Video autoplay blocked:', e));
      }
    }, [isStreaming, message.content]);

    // All three memos sit above the early returns below: hooks cannot run
    // conditionally, and this component returns early while a message is still
    // streaming with no content yet.
    const references = useMemo(
      () => transformReferences(message.references),
      [message.references],
    );

    // Derived from the very list the cards render, and numbered the same way, so
    // a badge and card N always name the same source.
    const citations = useMemo<CitationSource[]>(
      () =>
        references.map((ref, index) => ({
          number: index + 1,
          name: ref.name,
          url: ref.url,
        })),
      [references],
    );

    // Memoised because `message.content` grows with every streamed chunk: called
    // inline, this would rescan the whole accumulated answer on each one, which is
    // quadratic in the final length.
    const linkedContent = useMemo(() => linkifyCitations(message.content), [message.content]);

    // Show loading state when streaming but no content yet (during ingest/assessment stages)
    if (isStreaming && !message.content) {
      const isGrading = message.streamingStage === 'grading';
      const gradingPct = isGrading && message.gradingTotal && message.gradingTotal > 0
        ? Math.round((message.gradingCurrent ?? 0) / message.gradingTotal * 100)
        : null;

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 1,
            px: 1.5,
            py: 1,
            marginBottom: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              fontFamily: 'Readex Pro',
              '@keyframes colorPulse': {
                '0%, 100%': { color: '#9e9e9e' },
                '50%': { color: '#e0e0e0' },
              },
              animation: 'colorPulse 2s ease-in-out infinite',
            }}
          >
            {STREAMING_STAGE_LABELS[message.streamingStage ?? ''] ?? STREAMING_STAGE_FALLBACK}
            {gradingPct !== null && ` ${gradingPct}%`}
          </Typography>
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            width={48}
            height={48}
            style={{ display: 'block' }}
            src="/SynWave.webm"
          />
        </Box>
      );
    }

    if (!message.content) return null;

    const dynamiDirection = detectDirection(message.content);

    return (
      <NoFlipBox
        mode={dynamiDirection}
        sx={{
          display: 'flex',
          marginBottom: 3,
          '@keyframes answerIn': {
            from: { opacity: 0, transform: 'translateY(16px)' },
            to: { opacity: 1, transform: 'none' },
          },
          animation: 'answerIn .38s cubic-bezier(.22,.8,.3,1) both',
        }}
      >
        <NoFlipPaper
          elevation={0}
          sx={{
            width: '100%',
            backgroundColor: 'transparent',
            borderRadius: '0px',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
          mode={dynamiDirection}
        >
          <Markdown
            citations={citations}
            sx={{
              fontSize: '0.906rem',
              fontWeight: 400,
              lineHeight: 1.75,
              color: '#1F1F1F',
              fontFamily: 'Readex Pro',
              unicodeBidi: 'plaintext',
              '& p': { marginBottom: '12px' },
            }}
          >
            {linkedContent}
          </Markdown>

          <References data={references} />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mt: 1.75,
            }}
          >
            <CopyButton textToCopy={message.content} />
            {onFeedback && (
              <FeedbackButtons
                messageId={message.id}
                currentFeedback={message.feedback?.rating}
                onFeedback={onFeedback}
                isSubmitting={isFeedbackSubmitting}
              />
            )}
          </Box>
        </NoFlipPaper>
      </NoFlipBox>
    );
  },
);
